(() => {
  if (customElements.get('hx-review-sort')) return;
  let nextId = 0;
  const order = ['created_at', 'most_helpful', 'highest_rating', 'lowest_rating'];
  const excluded = ['pictures_first', 'with_pictures', 'videos_first'];
  class HexagonReviewSort extends HTMLElement {
    connectedCallback() { queueMicrotask(() => this.setup()); }
    setup() {
      if (this.controller || !this.isConnected) return;
      this.native = this.querySelector('select.jdgm-sort-dropdown');
      if (!this.native) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.native.hidden = true;
      this.native.classList.add('hx-review-sort-native');
      this.native.tabIndex = -1;
      this.native.setAttribute('aria-hidden', 'true');
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'hx-review-sort-trigger';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.label = document.createElement('span');
      this.trigger.append(this.label);
      const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      arrow.setAttribute('viewBox', '0 0 20 20');
      arrow.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm5 7.5 5 5 5-5');
      arrow.append(path);
      this.trigger.append(arrow);
      this.list = document.createElement('ul');
      this.list.className = 'hx-review-sort-list';
      this.list.id = `hx-review-sort-${++nextId}`;
      this.list.hidden = true;
      this.list.setAttribute('role', 'listbox');
      this.trigger.setAttribute('aria-controls', this.list.id);
      this.append(this.trigger, this.list);
      this.render();
      this.trigger.addEventListener('click', () => this.setOpen(this.list.hidden), { signal });
      this.trigger.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setOpen(true, event.key === 'ArrowUp' ? -1 : 0);
        }
      }, { signal });
      this.list.addEventListener('click', event => {
        const option = event.target.closest('button[data-value]');
        if (!option || option.disabled) return;
        event.stopPropagation();
        this.native.value = option.dataset.value;
        this.native.dispatchEvent(new Event('input', { bubbles: true }));
        this.native.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
        this.setOpen(false, null, true);
      }, { signal });
      this.list.addEventListener('keydown', event => {
        const items = this.buttons.filter(button => !button.disabled);
        let index = items.indexOf(document.activeElement);
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.setOpen(false, null, true);
          return;
        }
        if (event.key === 'Tab') { this.setOpen(false); return; }
        if (event.key === 'ArrowDown') index = (index + 1) % items.length;
        else if (event.key === 'ArrowUp') index = (index + items.length - 1) % items.length;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = items.length - 1;
        else return;
        event.preventDefault();
        items[index]?.focus();
      }, { signal });
      this.native.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.setOpen(false);
      }, { signal });
      this.optionObserver = new MutationObserver(() => this.render());
      this.optionObserver.observe(this.native, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['label', 'selected', 'disabled'] });
    }
    render() {
      const options = [...this.native.options].filter(option => !excluded.includes(option.value));
      options.sort((a, b) => {
        const ai = order.indexOf(a.value), bi = order.indexOf(b.value);
        return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
      });
      this.list.replaceChildren();
      this.buttons = options.map(option => {
        const item = document.createElement('li');
        item.setAttribute('role', 'presentation');
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.value = option.value;
        button.textContent = option.textContent;
        button.disabled = option.disabled;
        button.tabIndex = -1;
        button.setAttribute('role', 'option');
        item.append(button);
        this.list.append(item);
        return button;
      });
      if (excluded.includes(this.native.value)) {
        this.native.value = 'created_at';
        this.native.dispatchEvent(new Event('input', { bubbles: true }));
        this.native.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.sync();
    }
    sync() {
      this.label.textContent = this.native.selectedOptions[0]?.textContent || '';
      this.buttons.forEach(button => button.setAttribute('aria-selected', String(button.dataset.value === this.native.value)));
    }
    setOpen(open, focusIndex = null, restoreFocus = false) {
      this.list.hidden = !open;
      this.trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        const items = this.buttons.filter(button => !button.disabled);
        let focus = items.find(button => button.dataset.value === this.native.value) || items[0];
        if (focusIndex === -1) focus = items.at(-1);
        else if (focusIndex !== null) focus = items[focusIndex];
        focus?.focus();
      } else if (restoreFocus) this.trigger.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.optionObserver?.disconnect();
      this.trigger?.remove();
      this.list?.remove();
      if (this.native) {
        this.native.hidden = false;
        this.native.removeAttribute('aria-hidden');
        this.native.removeAttribute('tabindex');
      }
      this.controller = null;
    }
  }
  customElements.define('hx-review-sort', HexagonReviewSort);
  const selector = 'select.jdgm-sort-dropdown';
  const pending = new Set();
  let scheduled = false;
  const upgrade = () => {
    scheduled = false;
    const selects = [...pending];
    pending.clear();
    selects.forEach(select => {
      if (!select.isConnected || !select.matches(selector) || select.closest('hx-review-sort')) return;
      const shell = document.createElement('hx-review-sort');
      select.replaceWith(shell);
      shell.append(select);
    });
  };
  const collect = node => {
    if (node.nodeType !== 1 || node.closest('hx-review-sort')) return;
    if (node.matches(selector)) pending.add(node);
    node.querySelectorAll(selector).forEach(select => {
      if (!select.closest('hx-review-sort')) pending.add(select);
    });
  };
  const observer = new MutationObserver(records => {
    records.forEach(record => {
      if (record.target.nodeType === 1 && record.target.closest('hx-review-sort')) return;
      if (record.type === 'attributes') {
        if (record.target.nodeName === 'SELECT' && record.target.matches(selector)) pending.add(record.target);
        return;
      }
      record.addedNodes.forEach(collect);
    });
    if (!pending.size || scheduled) return;
    scheduled = true;
    requestAnimationFrame(upgrade);
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  document.querySelectorAll(selector).forEach(select => pending.add(select));
  upgrade();
})();

